'use client';

import * as React from 'react';
import {
  FiCalendar,
  FiClock as _FiClock,
  FiTrendingUp,
  FiAward,
  FiStar,
  FiX,
} from 'react-icons/fi';

import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Separator as _Separator,
  SimpleGrid,
  Badge,
  IconButton,
  Tabs,
  Input,
  Icon,
} from '../../index';
import {
  DateRangePreset,
  SavedRange,
  formatDateRange,
  createPresetRanges,
} from '../../utils/date-utils';

export interface QuickPresetsProps {
  /** Currently selected date range */
  value?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  /** Called when a preset is selected */
  onSelect: (range: { startDate: Date | null; endDate: Date | null }) => void;
  /** Custom preset ranges */
  presets?: DateRangePreset[];
  /** Saved ranges */
  savedRanges?: SavedRange[];
  /** Called when a saved range is deleted */
  onDeleteSavedRange?: (id: string) => void;
  /** Locale for display */
  locale?: string;
  /** Timezone for dates */
  timezone?: string;
  /** Maximum height for scrolling */
  maxHeight?: string | number;
  /** Whether to show search */
  showSearch?: boolean;
}

/**
 * QuickPresets component for date range selection
 * Provides categorized preset options and saved ranges
 */
export const QuickPresets: React.FC<QuickPresetsProps> = ({
  value,
  onSelect,
  presets,
  savedRanges = [],
  onDeleteSavedRange,
  locale = 'en-US',
  timezone = 'Europe/Istanbul',
  maxHeight,
  showSearch = true,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedTab, setSelectedTab] = React.useState('presets');

  // Helper to safely get timestamp from Date or string (handles JSON deserialization)
  const getTimestamp = React.useCallback(
    (date: Date | string | null | undefined): number | null => {
      if (!date) return null;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') return new Date(date).getTime();
      return null;
    },
    []
  );

  // Default presets if not provided
  const defaultPresets = React.useMemo(
    () => presets || createPresetRanges(locale),
    [presets, locale]
  );

  // Group presets by category
  const presetCategories = React.useMemo(() => {
    const categories = new Map<string, DateRangePreset[]>();

    defaultPresets.forEach((preset) => {
      const category = preset.category || 'common';
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)!.push(preset);
    });

    return categories;
  }, [defaultPresets]);

  // Filter presets based on search
  const filteredPresets = React.useMemo(() => {
    if (!searchQuery) return defaultPresets;

    const query = searchQuery.toLowerCase();
    return defaultPresets.filter(
      (preset) =>
        preset.label.toLowerCase().includes(query) ||
        preset.description?.toLowerCase().includes(query)
    );
  }, [defaultPresets, searchQuery]);

  // Filter saved ranges based on search
  const filteredSavedRanges = React.useMemo(() => {
    if (!searchQuery) return savedRanges;

    const query = searchQuery.toLowerCase();
    return savedRanges.filter(
      (range) => range.name.toLowerCase().includes(query) || range.emoji?.includes(query)
    );
  }, [savedRanges, searchQuery]);

  // Category icons
  const categoryIcons = {
    common: FiCalendar,
    business: FiTrendingUp,
    year: FiAward,
    custom: FiStar,
  };

  // Category labels
  const categoryLabels = {
    common: locale.startsWith('tr') ? 'Sık Kullanılan' : 'Common',
    business: locale.startsWith('tr') ? 'İş Dönemleri' : 'Business Periods',
    year: locale.startsWith('tr') ? 'Yıllık' : 'Year',
    custom: locale.startsWith('tr') ? 'Özel' : 'Custom',
  };

  const handlePresetClick = (preset: DateRangePreset) => {
    const range = preset.getValue(timezone);
    onSelect(range);
  };

  const handleSavedRangeClick = (savedRange: SavedRange) => {
    onSelect(savedRange.range);
  };

  return (
    <Box>
      {/* Search */}
      {showSearch && (
        <Box marginBottom={4}>
          <Input
            placeholder={locale.startsWith('tr') ? 'Ara...' : 'Search...'}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            leftIcon={<FiCalendar size={16} />}
            inputSize="sm"
          />
        </Box>
      )}

      {/* Tabs */}
      <Tabs.Root value={selectedTab} onValueChange={setSelectedTab}>
        <Tabs.List>
          <Tabs.Trigger value="presets">
            <HStack gap={2}>
              <Icon asChild fontSize="md">
                <FiCalendar />
              </Icon>
              <Text>{locale.startsWith('tr') ? 'Hızlı Seçim' : 'Quick Select'}</Text>
            </HStack>
          </Tabs.Trigger>
          {savedRanges.length > 0 && (
            <Tabs.Trigger value="saved">
              <HStack gap={2}>
                <Icon asChild fontSize="md">
                  <FiStar />
                </Icon>
                <Text>
                  {locale.startsWith('tr') ? 'Kayıtlı' : 'Saved'}{' '}
                  <Badge size="sm" colorPalette="gray">
                    {savedRanges.length}
                  </Badge>
                </Text>
              </HStack>
            </Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.ContentGroup>
          {/* Presets Tab */}
          <Tabs.Content value="presets">
            <Box maxHeight={maxHeight || '400px'} overflowY="auto" paddingTop={4}>
              {filteredPresets.length === 0 ? (
                <Box padding={8} textAlign="center">
                  <Text color="fg.muted">
                    {locale.startsWith('tr') ? 'Sonuç bulunamadı' : 'No results found'}
                  </Text>
                </Box>
              ) : (
                <VStack gap={6} align="stretch">
                  {Array.from(presetCategories.entries()).map(([category, categoryPresets]) => {
                    // Filter category presets by search
                    const filteredCategoryPresets = categoryPresets.filter((preset) =>
                      filteredPresets.some((fp) => fp.id === preset.id)
                    );

                    if (filteredCategoryPresets.length === 0) return null;

                    const Icon = categoryIcons[category as keyof typeof categoryIcons];

                    return (
                      <VStack key={category} gap={3} align="stretch">
                        <HStack gap={2} align="center">
                          <Icon size={14} />
                          <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
                            {categoryLabels[category as keyof typeof categoryLabels]}
                          </Text>
                        </HStack>
                        <SimpleGrid columns={2} gap={2}>
                          {filteredCategoryPresets.map((preset) => {
                            const presetRange = preset.getValue(timezone);
                            const isSelected =
                              value?.startDate &&
                              value?.endDate &&
                              presetRange.startDate?.getTime() === getTimestamp(value.startDate) &&
                              presetRange.endDate?.getTime() === getTimestamp(value.endDate);

                            return (
                              <Button
                                key={preset.id}
                                variant={isSelected ? 'primary' : 'outline'}
                                size="sm"
                                onClick={() => handlePresetClick(preset)}
                              >
                                <VStack gap={1} align="flex-start" width="100%">
                                  <Text fontSize="sm" fontWeight="medium">
                                    {preset.label}
                                  </Text>
                                  {preset.description && (
                                    <Text fontSize="xs" color="fg.muted" truncate>
                                      {preset.description}
                                    </Text>
                                  )}
                                </VStack>
                              </Button>
                            );
                          })}
                        </SimpleGrid>
                      </VStack>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Tabs.Content>

          {/* Saved Ranges Tab */}
          <Tabs.Content value="saved">
            <Box maxHeight={maxHeight || '400px'} overflowY="auto" paddingTop={4}>
              {filteredSavedRanges.length === 0 ? (
                <Box padding={8} textAlign="center">
                  <Text color="fg.muted">
                    {locale.startsWith('tr')
                      ? searchQuery
                        ? 'Sonuç bulunamadı'
                        : 'Kayıtlı aralık bulunamadı'
                      : searchQuery
                        ? 'No results found'
                        : 'No saved ranges'}
                  </Text>
                </Box>
              ) : (
                <VStack gap={2} align="stretch">
                  {filteredSavedRanges.map((savedRange) => {
                    const isSelected =
                      value?.startDate &&
                      value?.endDate &&
                      savedRange.range.startDate?.getTime() === getTimestamp(value.startDate) &&
                      savedRange.range.endDate?.getTime() === getTimestamp(value.endDate);

                    return (
                      <Box
                        key={savedRange.id}
                        padding={3}
                        borderRadius="md"
                        backgroundColor={isSelected ? 'bg.subtle' : 'transparent'}
                        border="1px solid"
                        borderColor={isSelected ? 'border.default' : 'transparent'}
                        _hover={{ backgroundColor: 'bg.subtle' }}
                      >
                        <HStack justifyContent="space-between" align="flex-start">
                          <VStack
                            gap={1}
                            align="flex-start"
                            flex={1}
                            onClick={() => handleSavedRangeClick(savedRange)}
                            cursor="pointer"
                          >
                            <HStack gap={2} align="center">
                              {savedRange.emoji && <Text fontSize="lg">{savedRange.emoji}</Text>}
                              <Text fontSize="sm" fontWeight="medium">
                                {savedRange.name}
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color="fg.muted">
                              {formatDateRange(savedRange.range, {
                                locale,
                                timezone,
                              })}
                            </Text>
                          </VStack>
                          {onDeleteSavedRange && (
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              icon={<FiX size={14} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteSavedRange(savedRange.id);
                              }}
                              aria-label={locale.startsWith('tr') ? 'Sil' : 'Delete'}
                            />
                          )}
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>
    </Box>
  );
};
